/**
 * "Your next 3 things" (core journey.ts personalPath): the personal path card on Today, for a business (its location) or a
 * home (its profile). Only the next 3 show, best benefit per minute first; each says why, then one button. "Done" and
 * "Not now" move an item out of the top; "See everything" keeps the rest one tap away, folded.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { personalPath, whoOfPlace, type HomeProfile, type JourneyRole, type PathContext, type PathItem } from "@logwave/core";
import { isHomeOrg, type JourneyPatch } from "../api.ts";
import { Icon } from "./Icons.tsx";
import { Button, Card, Section, useToast } from "./ui.tsx";
import { useLoad } from "../lib/useLoad.ts";
import { useApi, useSession, type Session } from "../session.tsx";

/* ─────────────── the path's context, from what the app knows ─────────────── */

const ROLE_OF: Record<string, JourneyRole> = { owner: "owner", admin: "owner", manager: "manager", staff: "staff" };

export function businessContext(s: Session): PathContext {
  const loc = s.location;
  return {
    who: whoOfPlace({ orgKind: s.org.kind, permitType: loc.permitType, typeId: loc.journey?.typeId }),
    // A place set up before the journey (no stage) was set up to run it: open, never "planning".
    stage: loc.openingPhase ?? "open_running",
    role: ROLE_OF[s.role] ?? "staff",
    unconfirmed: loc.unconfirmed ?? [],
    journey: loc.journey,
  };
}
export function homeContext(p: HomeProfile | undefined): PathContext {
  return { who: "home", stage: p?.stage, household: { kids: (p?.household.kids ?? 0) > 0, guests: !!p?.guests }, journey: p?.journey };
}

/** Loads the path for the current workspace and marks items (business: the location; home: the profile). */
export function usePath() {
  const api = useApi();
  const s = useSession();
  const home = isHomeOrg(s.org);
  const profile = useLoad(() => (home ? api.homeProfile(s.org.id) : Promise.resolve(undefined)), [api, s.org.id, home]);
  const ctx = home ? homeContext(profile.data) : businessContext(s);
  const view = useMemo(() => personalPath(ctx), [JSON.stringify(ctx)]); // eslint-disable-line react-hooks/exhaustive-deps
  async function patch(body: JourneyPatch) {
    if (home) { const p = await api.patchHomeJourney(s.org.id, body); profile.setData(p); return; }
    const loc = await api.patchJourney(s.org.id, s.location.id, body);
    s.patchLocation(loc);
  }
  return { home, ready: !home || !!profile.data, ctx, view, patch, profile };
}

/* ─────────────── "Your next 3 things" ─────────────── */

export function PathCard({ title = "Your next 3 things" }: { title?: string }) {
  const p = usePath();
  const toast = useToast();
  const nav = useNavigate();
  const [busy, setBusy] = useState<string>();
  if (!p.ready) return null;
  const { next, later, done, total } = p.view;
  if (!next.length && !later.length) return null;
  async function mark(item: PathItem, how: "done" | "later" | "undo") {
    setBusy(item.id);
    try { await p.patch({ item: item.id, how }); if (how === "done") toast.show(`Done: ${item.title}`, { icon: "check" }); }
    catch (e) { toast.show(e instanceof Error ? e.message : String(e), { icon: "alert" }); }
    finally { setBusy(undefined); }
  }
  return (
    <Section title={title} kicker={`${done.length} of ${total} done`} className="path">
      <ol className="plain stack" data-testid="path-next">
        {next.map((item, n) => (
          <li key={item.id}>
            <Card className="stack-sm path-item" edge={n === 0 ? "info" : undefined}>
              <h3>{item.title}</h3>
              <p className="small"><strong>Why:</strong> {item.why}</p>
              <div className="row">
                <Button big={n === 0} icon="chevron" iconEnd onClick={() => nav(item.action.to)}>{item.action.label}</Button>
                <Button variant="quiet" small icon="check" busy={busy === item.id} onClick={() => void mark(item, "done")} aria-label={`Done: ${item.title}`}>Mark it done</Button>
                <Button variant="quiet" small onClick={() => void mark(item, "later")} aria-label={`Not now: ${item.title}`}>Not now</Button>
              </div>
              <p className="caption">About {item.minutes} min{item.source === "Our advice" ? " · Our advice" : ""}</p>
            </Card>
          </li>
        ))}
      </ol>
      {(later.length > 0 || done.length > 0) && (
        <details className="card" data-testid="path-all">
          <summary><strong>See everything ({later.length + done.length} more)</strong></summary>
          <ul className="plain stack-sm">
            {later.map((item) => (
              <li key={item.id} className="row">
                <span><strong>{item.title}</strong><br /><span className="small muted">{item.why}</span></span>
                <Button variant="quiet" small onClick={() => nav(item.action.to)}>{item.action.label}</Button>
              </li>
            ))}
            {done.map((item) => (
              <li key={item.id} className="row">
                <span className="muted"><Icon name="check" size={16} /> {item.title}</span>
                <Button variant="quiet" small onClick={() => void mark(item, "undo")} aria-label={`Undo: ${item.title}`}>Undo this</Button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </Section>
  );
}

